import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Paperclip, Send } from "lucide-react";
import { chipToast } from "@/components/ui/chip-toast";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

const controlClass =
  "h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)";

export function ContactSupportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const [topic, setTopic] = useState("support");
  const [priority, setPriority] = useState("normal");
  const [message, setMessage] = useState("");

  const handleSubmit = () => {
    // No support backend yet (deferred) — presentational confirmation.
    chipToast({
      message: t`Ticket submitted successfully. We'll contact you soon.`,
      actionLabel: t`View status`,
    });
    setMessage("");
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setMessage("");
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Contact support</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Tell us what&rsquo;s going on and our team will get back to you.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t`Topic`}>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger className={controlClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">{t`Support request`}</SelectItem>
                  <SelectItem value="billing">{t`Billing question`}</SelectItem>
                  <SelectItem value="bug">{t`Bug report`}</SelectItem>
                  <SelectItem value="feature">{t`Feature request`}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t`Priority`}>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className={controlClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t`Low`}</SelectItem>
                  <SelectItem value="normal">{t`Normal`}</SelectItem>
                  <SelectItem value="high">{t`High`}</SelectItem>
                  <SelectItem value="urgent">{t`Urgent`}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="support-message"
              className="text-xs font-medium text-(--color-text-primary)"
            >
              <Trans>Message</Trans>
            </label>
            <div className="flex flex-col rounded-lg border border-(--color-border) bg-(--color-surface-content) focus-within:border-(--color-ring)">
              <textarea
                id="support-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t`Describe the issue etc...`}
                className="min-h-40 w-full resize-none bg-transparent px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
              />
              <div className="px-3 pb-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
                >
                  <Paperclip className="size-5" />
                  <Trans>Attach files</Trans>
                </button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={message.trim().length === 0}
          >
            <Send />
            <Trans>Submit</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
